import React, { useContext, useEffect, useState } from "react";
import { FormattedHTMLMessage } from "react-intl-phraseapp";
import { AppStore } from "../../../app/store";
import {
  NewRelicPageActionNames,
  PlanCategoryFilters,
  PlanType,
} from "../../../@types";
import {
  addNewRelicPageAction,
  CONSTANTS,
  useTranslate,
} from "../../../helpers";
import { CCXPBaseDrop, HelpDrawerWrapper } from "../../../components";
import { default as C, SearchFilterKeys } from "./constants";
import { ChoiceList } from "@cmsgov/ds-medicare-gov";
import { searchResultsFilterAnalytics } from "./helpers";
import { AnalyticsActionType } from "../../../app/contexts/Analytics/types";

export interface Props {
  basicMode?: boolean;
  categories?: PlanCategoryFilters;
  onFilterUpdate: (filters: string[], swapType?: boolean) => void;
}

const PlanCategoriesFilter: React.FunctionComponent<Props> = ({
  basicMode,
  categories = {},
  onFilterUpdate,
}) => {
  const {
    state: { language, searchResultsFilters, planType },
    dispatch,
  } = useContext(AppStore.AppContext);
  const t = useTranslate();
  const [currentSelections, setCurrentSelections] = useState(
    searchResultsFilters.planCategoriesFilter
  );
  const [selectedPlanType, setSelectedPlanType] = useState(planType);

  // Reference a wrapper around the component
  const [node, planCategoriesFilterRef] = useState<HTMLDivElement | null>(null);

  // Use the referenced wrapper on mount to remove aria attributes from DS components
  // to lessen the amount of screen-reader noise
  useEffect(() => {
    if (!node) {
      return;
    }
    if (node) {
      node.querySelectorAll("[aria-live]").forEach(ariaLiveEl => {
        ariaLiveEl.removeAttribute("aria-live");
        ariaLiveEl.removeAttribute("aria-relevant");
        ariaLiveEl.removeAttribute("aria-atomic");
      });
    }
  }, [node]);

  const helpDrawer = (
    <HelpDrawerWrapper
      toggleText={t("search_results_page.filters.types_of_plans.help.toggle")}
      drawerTitle={t("search_results_page.filters.types_of_plans.help.title")}
      tealiumProps={{
        callingLabel: "Find Plans - Available Plans",
        label: "Learn about plan types",
      }}
    >
      <p>{t("search_results_page.filters.types_of_plans.help.intro")}</p>
      <p>
        <b>{t("search_results_page.filter_labels.hmo")}:</b>{" "}
        {t("search_results_page.filters.types_of_plans.help.hmo")}
      </p>
      <p>
        <b>{t("search_results_page.filter_labels.ppo")}:</b>{" "}
        {t("search_results_page.filters.types_of_plans.help.ppo")}
      </p>
      <p>
        <b>{t("search_results_page.filter_labels.pffs")}:</b>{" "}
        {t("search_results_page.filters.types_of_plans.help.pffs")}
      </p>
      <p>
        <b>{t("search_results_page.filter_labels.msa")}:</b>{" "}
        {t("search_results_page.filters.types_of_plans.help.msa")}
      </p>
      <p>
        <b>{t("search_results_page.filter_labels.cost")}:</b>{" "}
        {t("search_results_page.filters.types_of_plans.help.cost")}
      </p>
      <ul>
        <li>
          <FormattedHTMLMessage id="search_results_page.filters.types_of_plans.help.cost_2" />
        </li>
        <li>
          <FormattedHTMLMessage id="search_results_page.filters.types_of_plans.help.cost_3" />
        </li>
      </ul>
      <p>
        <b>{t("search_results_page.filter_labels.mmp")}:</b>{" "}
        {t("search_results_page.filters.types_of_plans.help.mmp")}
      </p>
      <p>
        <b>{t("search_results_page.filter_labels.special_needs")}:</b>{" "}
        {t("search_results_page.filters.types_of_plans.help.special_needs")}
      </p>
      <p>
        <a
          href={CONSTANTS.linkToTypesOfHealthPlans(language)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(): void => {
            dispatch({
              type: AnalyticsActionType.SEND_TEALIUM_EVENT,
              settings: {
                event_action:
                  "Find Plans - Available Plans - Learn about plan types",
                event_label: "learn more about Medicare health plans",
              },
            });
          }}
        >
          {t("search_results_page.filters.types_of_plans.help.learn_more_link")}
        </a>
      </p>
    </HelpDrawerWrapper>
  );

  useEffect(() => {
    setSelectedPlanType(planType);
  }, [planType]);

  useEffect(() => {
    setCurrentSelections(searchResultsFilters.planCategoriesFilter);
  }, [searchResultsFilters.planCategoriesFilter]);

  const label = t("search_results_page.filters.types_of_plans");
  const maChoices = Object.entries(categories)
    .filter(([_, numPlans]) => numPlans)
    .map(([cat]) => ({
      checked: !!currentSelections.includes(C.categoryFilterMap[cat]),
      label: t(C.categoryFilterTranslationMap[C.categoryFilterMap[cat]]),
      value: C.categoryFilterMap[cat],
    }));

  const choiceList = (
    <div ref={planCategoriesFilterRef}>
      <ChoiceList
        name={basicMode ? "PlanTypeFilterBasic" : "PlanTypeFilter"}
        type="radio"
        label={
          <p
            className={`PlanFilters__action__label ${
              basicMode ? "" : "ds-u-visibility--screen-reader"
            }`}
          >
            {label}
          </p>
        }
        className="ds-u-margin--0"
        onChange={event => {
          const { value } = event.currentTarget;
          setSelectedPlanType(value);
          if (basicMode) {
            onFilterUpdate(currentSelections, planType !== value);
          }
        }}
        choices={[
          {
            label: t("search_results_page.filters.medicare_advantage"),
            checked: selectedPlanType !== PlanType.PDP,
            value: PlanType.MAPD,
            checkedChildren: (
              <ChoiceList
                name={basicMode ? "MaCategoriesBasic" : "MaCategories"}
                type="checkbox"
                label={t("search_results_page.filters.types_of_health_plans")}
                hint={helpDrawer}
                className="ds-u-margin--0 ds-u-border-bottom--1"
                onChange={event => {
                  const { checked, value } = event.currentTarget;
                  const selected =
                    checked && !currentSelections.includes(value)
                      ? [...currentSelections, value]
                      : currentSelections.filter(f => f !== value);
                  setCurrentSelections(selected);
                  if (basicMode) {
                    onFilterUpdate(selected, planType !== selectedPlanType);
                  }
                }}
                choices={maChoices}
              />
            ),
          },
          {
            label: t("search_results_page.filters.prescription_drug_plans"),
            checked: selectedPlanType === PlanType.PDP,
            value: PlanType.PDP,
          },
        ]}
      />
    </div>
  );

  return basicMode ? (
    choiceList
  ) : (
    <>
      <p className="PlanFilters__action__label">{label}</p>
      <CCXPBaseDrop
        label={t("search_results_page.filters.select_plan_type")}
        onApply={() => {
          currentSelections.forEach(value => {
            addNewRelicPageAction(
              NewRelicPageActionNames.SEARCH_RESULTS_FILTER,
              {
                filter: "Plan Categories Filter",
                value: currentSelections.toString(),
              }
            );
            searchResultsFilterAnalytics({
              filterValue: value,
              filterType: SearchFilterKeys.planCategoriesFilter,
              dispatch,
            });
            dispatch({
              type: AnalyticsActionType.SEND_TEALIUM_EVENT,
              settings: {
                event_action: "Find Plans - Available Plans",
                event_label: value,
              },
            });
          });
          if (selectedPlanType === PlanType.PDP) {
            setCurrentSelections([]);
          }
          onFilterUpdate(currentSelections, planType !== selectedPlanType);
        }}
        onClear={() => {
          setCurrentSelections([]);
          setSelectedPlanType(planType);
        }}
        hasValue={false}
        size="wide"
      >
        {choiceList}
      </CCXPBaseDrop>
    </>
  );
};

export default PlanCategoriesFilter;
